import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Toolbar,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import * as Countries from 'i18n-iso-countries';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import ComboBox, { DefaultDataType } from '../layout/ComboBox';
import GenericDialog from '../GenericDialog';
import ContentBox from '../layout/ContentBox';
import InstituteEditDialog from './InstituteEditDialog';

import { convertToQid } from '../../utilities/stringHelper';
import { Institute, RootState } from '../../redux/types';
import {
  addUsersToInstitute,
  fetchInstitutes,
  updateInstitute,
  showNotification,
} from '../../redux/actions';

type InstituteDetail = {
  institute: Institute;
}

interface UserComboData extends DefaultDataType {
  label: string;
  value: string;
  userId: number;
}

const InstituteManageDetail: React.FC<InstituteDetail> = (props: InstituteDetail) => {
  const { institute } = props;
  const dispatch = useAppDispatch();
  const usersById = useAppSelector((state: RootState) => state.users.byId);
  const allUserIds = useAppSelector((state: RootState) => state.users.allIds);
  const institutesById = useAppSelector((state: RootState) => state.institute.byId);
  const locale = useAppSelector((state: RootState) => state.intl.locale);
  const [showAddUserDialog, setShowAddUserDialog] = useState<boolean>(false);
  const [showEditInstituteDialog, setShowEditInstituteDialog] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<UserComboData[]>([]);

  useEffect(() => {
    if (!institutesById) {
      dispatch(fetchInstitutes());
    }
  }, [dispatch, institutesById]);

  const updateInstituteAction = useCallback((inst: Institute) => {
    dispatch(updateInstitute(inst));
  }, [dispatch]);

  const showNotificationAction = useCallback((
    message: string | React.ReactElement,
  ) => dispatch(showNotification(message)), [dispatch]);

  const onOpenAddUserDialog = () => {
    setShowAddUserDialog(true);
  };

  const onCloseAddUserDialog = () => {
    setShowAddUserDialog(false);
    setSelectedUsers([]);
  };

  const onOpenEditInstituteDialog = () => {
    setShowEditInstituteDialog(true);
  };

  const onAddUsers = () => {
    if (!selectedUsers.length) {
      return;
    }
    const instId = institute.id;
    const payload = {
      userIds: selectedUsers.map((v) => v.userId),
    };
    dispatch(addUsersToInstitute(instId, payload));
    onCloseAddUserDialog();
  };

  const onSelectValue = (values: UserComboData[] | null) => {
    setSelectedUsers(values || []);
  };

  const userComboData: UserComboData[] | undefined | false = showAddUserDialog
    && usersById
    && allUserIds
    && institutesById
    && allUserIds.map((id) => {
      const user = usersById[id];
      const { instituteId, email } = user;
      const userInstitute = institutesById[instituteId];
      const convertedQid = convertToQid(id, userInstitute?.country || user.userCountry);

      return {
        label: `${convertedQid} - ${email}`,
        value: convertedQid,
        userId: id,
      } as UserComboData;
    });

  return (
    <>
      <Paper sx={{ flexGrow: 1, p: '3px 15px 10px 15px' }} variant="outlined">
        <Toolbar disableGutters>
          <Typography variant="h5" style={{ flexGrow: 1 }}>{institute.name}</Typography>
          <Button sx={{ mx: 0.5 }} variant="outlined" onClick={onOpenAddUserDialog}><FormattedMessage id="instituteUser.addUsers" /></Button>
          <Button sx={{ mx: 0.5 }} variant="outlined" onClick={onOpenEditInstituteDialog}><FormattedMessage id="edit" /></Button>
        </Toolbar>
        {
          institute.hospitalName
          && <b>{institute.hospitalName}</b>
        }
        <Box>{`${institute.street} ${institute.housenumber}`}</Box>
        <Box>{`${institute.city} ${institute.zip}`}</Box>
        <Box>{Countries.getName(institute.country, locale)}</Box>
        &nbsp;
        <Box>{institute.email}</Box>
        <Box>{institute.phone}</Box>
      </Paper>
      {/* Add User to Institute Dialog */}
      <GenericDialog
        isOpen={showAddUserDialog}
        closeButtonLabel={<FormattedMessage id="genericClose" />}
        onClose={onCloseAddUserDialog}
        maxWidth="sm"
        actions={[
          <Button
            variant="contained"
            color="primary"
            key="confirm"
            onClick={onAddUsers}
            sx={{ mx: 0.5 }}
          >
            <FormattedMessage id="confirm" />
          </Button>,
        ]}
      >
        <ContentBox title={<FormattedMessage id="instituteUser.addUsers" />} elevation={0}>
          <div style={{ height: '280px' }}>
            {
              userComboData
              && (
                <ComboBox<UserComboData>
                  keyId="institutes"
                  data={userComboData}
                  onChange={onSelectValue}
                  selectedItems={selectedUsers}
                  menuIsOpen
                  autoFocus
                  menuListHeight={200}
                  isMulti
                  mapLabels
                />
              )
            }
          </div>
        </ContentBox>
      </GenericDialog>
      {/* Edit Institute Dialog */}
      <InstituteEditDialog
        isOpen={showEditInstituteDialog}
        institute={institute}
        updateInstitute={updateInstituteAction}
        showNotification={showNotificationAction}
        onClose={() => { setShowEditInstituteDialog(false); }}
        locale={locale}
      />
    </>
  );
};

export default InstituteManageDetail;
